import CountdownTimer from '@/molecules/CountdownTimer';
import FormGroup from 'atoms/FormGroup';
import Heading from 'atoms/Heading';
import Text from 'atoms/Text';
import { ArrowRight, Check, Circle, Play } from 'react-feather';
import DialogBox from '@/molecules/DialogBox';
import { yupValidator } from 'lib/yup-validator';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { useCallback } from 'react';
import Decimal from 'decimal.js-light';
import MatchingChip from '@/molecules/MatchingChip';
import { isSpreadOrButterfly } from 'lib/constants';
import { socketEmit } from '@/services/socket';
import IRSUnit from './IRSUnit';
import { getMatchingCurveInterestDisplayName } from '@/helper/irsMatching.helper';
import useCommonStore from '@/store/commonStore';

const StatBox = ({ label, value, sub, color }) => (
    <div className="flex-1 border-r last:border-r-0 mr-2 last:mr-0 border-gray-500">
        <Text variant='caption' fontWeight='font-medium' className='text-gray-200 uppercase'>{label}</Text>
        <div className="flex gap-1 items-center mt-1">
            <Text variant='bodySmall' fontWeight='font-semibold' className={`${color || 'text-success-300'} uppercase`}>{value}</Text>
            {sub && <Text variant='caption' fontWeight='font-medium' className='text-gray-200 uppercase'>{sub}</Text>}
        </div>
    </div>
);
const defaultPriceValidation = {
    max: {
        price: 1000,
        message: `Price must be less than or equal to ${(1000).toLocaleString()}`
    },
    min: {
        price: -1000,
        message: `Price must be greater than or equal to ${(-1000).toLocaleString()}`
    }
};
const placesMap = {
    tenth: 0.1,
    hundredth: 0.01,
    thousandth: 0.001,
    tenThousandth: 0.0001,
    hundredThousandth: 0.00001
};
const PartialFillCard = ({ isPartial = false, metadata, close, pid, totalTime = 30, endTime }) => {
    const serverTime = useCommonStore((state) => state.serverTime);
    const timeRemaining = (new Date(endTime).getTime() - serverTime) / 1000;

    const getMaxPrice = useCallback(() => defaultPriceValidation.max, []);

    const getMinPrice = useCallback(() => defaultPriceValidation.min, []);
    const isBpsPrice = metadata && (metadata.futureName || (metadata.s && (isSpreadOrButterfly(metadata.s) || (metadata?.n?.endsWith('BOB') || metadata?.n?.endsWith('6s3s')))));

    const {
        formState: { errors },
        register,
        watch,
        getValues,
        setValue,
        handleSubmit
    } = useForm({
        resolver: yupValidator(
            yup.object().shape({
                price: yup
                    .number()
                    .typeError('Please enter a valid Price.')
                    .min(getMinPrice().price, getMinPrice().message)
                    .max(getMaxPrice().price, getMaxPrice().message)
                    .test(
                        'decimalPlaces',
                        'Price can have five decimal places.',
                        (value) => {
                            const decimalPlaces = value.toString().split('.')[1];
                            return !decimalPlaces || decimalPlaces.length <= 5;
                        },
                    )
                    .required('price is mandatory'),
                size: yup.mixed().test(
                    'Size',
                    `min size ${metadata.ms}K`,
                    (v) => v && v >= metadata.ms
                ).test(
                    'Quantity',
                    'size must be a multiple of 5',
                    (v) => v && v % 5 === 0
                )
            }),
        ),
        defaultValues: {
            price: metadata.p,
            size: metadata.q
        }
    });
    const updatePrice = (place, positive) => {
        if (!getValues('price')) setValue('price', 0);
        const price = new Decimal(getValues('price'));
        const newPrice = price.add(placesMap[place] * (positive ? 1 : -1));
        if (newPrice.lessThanOrEqualTo(-1e15) || newPrice.greaterThanOrEqualTo(1e15)) return;
        setValue('price', newPrice.toFixed(5));
    };
    const onConfirm = (d) => {
        if (Number(d.price) === Number(metadata.p)) {
            socketEmit('/post/irs-matchings/:id', {
                id: metadata.id,
                type: metadata.type === 'BID' ? 'buy' : 'sell',
                size: Number(d.size)
            });
            socketEmit('/put/irs-matching-popups', { id: pid });
        } else {
            socketEmit('/new-irs-matching-price', {
                id: metadata.id,
                irsId: metadata.irsId,
                price: d.price,
                size: d.size,
                type: Number(d.price) > Number(metadata.p) ? 'buy' : 'sell',
                popupId: pid,
                brokerType: metadata.bt,
                companyId: metadata.companyId
            });
        }
    };

    if (timeRemaining <= 0) return null;

    return (
        <DialogBox
            customPadding="p-8"
            bgColor='bg-gray-800 border border-gray-500'
            width="w-[574px]"
            skipClickOutside={true}
            className="z-[10001]">
            <div>

                <div className="flex flex-col gap-1 pb-6 border-b border-gray-500 mb-6">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <span className="w-2.5 h-2.5 bg-warning-200 rounded-full animate-flash" />
                            <Heading type="h6" className="font-semibold text-warning-200">{isPartial ? 'PARTIAL FILL - ACTION REQUIRED' : 'RECONFIRM INTEREST'}</Heading>
                        </div>
                        <MatchingChip theme={metadata.bt === 'IDB' ? 'idb' : 'agy'} />
                    </div>

                    <div className="flex items-center gap-2">
                        <Text variant='caption' className='text-gray-200'>{getMatchingCurveInterestDisplayName(metadata)}</Text>
                        <Text variant='caption' fontWeight='font-semibold' className='text-gray-200 border border-gray-500 px-1.5 py-0.5 rounded-sm'>{metadata.s}</Text>
                    </div>
                </div>
                {isPartial
                    ? <>
                        <div className="flex px-3 py-[9px] border border-success-300/25 rounded-md overflow-hidden bg-success-300/10 mb-4">
                            <StatBox label="Filled Qty" value={`${metadata.ct}K`} />
                            <StatBox label="Fill Rate" value={metadata.p} />
                            <StatBox label="Remaining" value={`${metadata.q}K`} sub={metadata.type} />
                        </div>
                        <div className="text-base text-gray-200 mb-4">
                            <span className="text-warning-200 font-semibold">{metadata.ct}K filled</span> at{' '}
                            <span className="text-warning-200 font-semibold">{metadata.p}</span>. Confirm to post the remaining{' '}
                            <span className="text-warning-200 font-semibold">{metadata.q}K offer</span> at{' '}
                            <span className="text-warning-200 font-semibold">{metadata.p}</span>, or adjust the price below and submit or cancel to void.
                        </div>
                    </>
                    : <>
                        <div className="w-full rounded-xl border border-warning-200/25 bg-warning-200/10 px-3 py-2 mb-3">
                            <div className="flex items-center gap-2">
                                <div className="w-[46%]">
                                    <Text variant='caption' fontWeight='font-medium' className='text-warning-300 uppercase'>PREVIOUS PRICE</Text>
                                    <Text variant='bodySmall' fontWeight='font-semibold' className={'text-gray-200 line-through mt-1'}>{metadata.op}</Text>
                                </div>
                                <ArrowRight size={22} className="text-warning-200" />

                                <div className="">
                                    <Text variant='caption' fontWeight='font-medium' className='text-warning-300'>NEW PRICE</Text>
                                    <Heading type="h6" className="text-warning-200 font-semibold py-1">{metadata.p}</Heading>
                                    <div className="inline-flex items-center gap-1 border border-warning-200/25 bg-warning-200/10 px-1 py-0 rounded-sm">
                                        <Play size={13} className={`${metadata.p > metadata.op ? '-rotate-90' : 'rotate-90'} fill-warning-200 text-warning-200`} />
                                        <Text variant='bodySmall' className={'text-warning-200'}>{metadata.p > metadata.op ? '+' : ''} {Number(((metadata.p - metadata.op) * 100).toFixed(3))}bp</Text>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="flex gap-2 items-center mb-3">
                            <div className="border border-primary-100/25 bg-primary-110 flex items-center gap-1 px-1.5 py-0.5 rounded">
                                <div className="w-1.5 h-1.5 rounded bg-primary-100" />
                                <Text variant='small' className={'text-primary-100'}>{metadata.type === 'BID' ? 'Bid' : 'Offer'}</Text>
                            </div>
                            <Text className={'text-gray-200'}>The mid has been repriced by another market participant.</Text>
                        </div>
                        <div className="text-base text-gray-200 mb-4">
                            The mid has been repriced by another market participant from{' '}
                            <span className="text-warning-200 font-semibold">{metadata.op}</span> to{' '}
                            <span className="text-warning-200 font-semibold">{metadata.p}</span>. Confirm to resubmit your{' '}
                            <span className="text-gray-100 font-semibold">{metadata.q}K {metadata.type.toLowerCase()}</span> at the new mid of{' '}
                            <span className="text-success-300 font-semibold">{metadata.p}</span>, or adjust your size below or cancel to void.
                        </div>
                    </>
                }

                <div className="flex gap-3 mb-6 pb-6 border-b border-gray-500">
                    <div className="flex-1">
                        <Text variant='bodySmall' fontWeight='font-medium' className='text-gray-200'>Mid Price</Text>
                        {isPartial
                            ? <>
                                <div className="relative my-2">
                                    <FormGroup
                                        label={false}
                                        name="price"
                                        register={register}
                                        errors={errors}
                                        autoFocus={true}
                                        placeholder={'0.0000'}
                                        className="flex-col !gap-2"
                                        labelWidth="!text-warning-200"
                                        errorWidth={false}
                                        onkeydown={() => { }}
                                        currency={isBpsPrice ? 'bps' : '%'}
                                        currencyClass="!w-[30px]"
                                        inputClass='hover:border-success-500 focus:border-success-500 w-full'
                                    />
                                    <div className="flex items-center gap-x-[1px] absolute right-[32px] top-0.5 h-9">
                                        <IRSUnit
                                            modifiers={[
                                                ['thousandth', 'thousandth']
                                            ]}
                                            incrementFunc={(e) => updatePrice(e, true)}
                                            decrementFunc={(e) => updatePrice(e, false)}

                                        />
                                    </div>
                                </div>
                                <Text variant='bodySmall' className={`${Number(watch('price')) > Number(metadata.p) ? 'text-success-300' : Number(watch('price')) === Number(metadata.p) ? 'text-gray-200' : 'text-error-300'} flex items-center gap-1`}> <Play size={14} className="-rotate-90 fill-gray-200" /> vs fill: {Number(watch('price')) > Number(metadata.p) ? '+' : ''} {Number(((Number(watch('price')) - Number(metadata.p)) * 100).toFixed(3))}bp</Text>
                            </>
                            : <>
                                <Text variant='bodySmall' className='text-gray-100 border border-gray-500 px-3.5 py-2.5 h-10 rounded-md my-2'>{metadata.p} {isBpsPrice ? 'bps' : '%'}</Text>
                                <Text variant='bodySmall' className='text-gray-300 flex items-center gap-1.5'>Set by market <Circle size={5} className="fill-gray-300 text-gray-300" /> Not editable</Text>

                            </>
                        }
                    </div>

                    <div className="flex-1">
                        <Text variant='bodySmall' fontWeight='font-medium' className={`${Number(watch('price')) > Number(metadata.p) ? 'text-success-300' : Number(watch('price')) < Number(metadata.p) ? 'text-error-300' : metadata.type === 'BID' ? 'text-success-300' : 'text-error-300'}`}
                        >{Number(watch('price')) > Number(metadata.p) ? 'Bid' : Number(watch('price')) < Number(metadata.p) ? 'Offer' : metadata.type === 'BID' ? 'Bid' : 'Offer'} Size</Text>
                        <div className="relative my-2">
                            <FormGroup
                                label={false}
                                name="size"
                                register={register}
                                errors={errors}
                                autoFocus={true}
                                placeholder={'0.0000'}
                                className="flex-col !gap-2"
                                labelWidth="!text-warning-200"
                                errorWidth={false}
                                onkeydown={() => { }}
                                currency="USD (K)"
                                currencyClass="!w-[56px]"
                                inputClass='hover:border-success-500 focus:border-success-500 w-full'
                            />
                            <div className="flex items-center gap-x-[1px] absolute right-[57px] top-0.5 h-9">
                                <IRSUnit
                                    modifiers={[
                                        ['tenth', 'tenth']
                                    ]}
                                    incrementFunc={() => { setValue('size', Number(watch('size') || 0) + 5); }}
                                    decrementFunc={() => { setValue('size', Math.max(Number(watch('size') || 0) - 5, metadata.ms)); }}

                                />
                            </div>
                        </div>
                        {isPartial && <Text variant='bodySmall' className={`${watch('size') >= metadata.mrs ? 'text-success-300' : 'text-gray-200'} flex items-center gap-1`}><Check size={14} /> Meets min { }{metadata.mrs}K</Text>}
                    </div>
                </div>

                <div className="flex gap-4">
                    <button className="flex-1 border border-error-300 active:scale-95 text-base font-semibold text-gray-100 h-11 rounded-md bg-error-300/15 flex items-center justify-center gap-2"
                        onClick={() => {
                            socketEmit('/put/irs-matching-popups', { id: pid });
                        }}>
                        <CountdownTimer
                            seconds={Math.round(timeRemaining)} total={totalTime}
                            onComplete={() => { close(); }}
                        />
                        Cancel & Void
                    </button>

                    <button className="flex-1 bg-success-300 text-gray-100 h-11 rounded-md text-base font-semibold active:scale-95"
                        onClick={() => handleSubmit((data) => onConfirm(data))()}>
                        Confirm {isPartial ? `${Number(watch('price')) > Number(metadata.p) ? 'Bid' : Number(watch('price')) < Number(metadata.p) ? 'Offer' : metadata.type === 'BID' ? 'Bid' : 'Offer'}` : metadata.type === 'BID' ? 'Bid' : 'Offer'}
                    </button>
                </div>
            </div>
        </DialogBox>
    );
};

export default PartialFillCard;
